import { useCallback, useMemo, useState } from "react";

import { useTableViewPagination } from "@covertree/hooks";
import { ROWS_PER_PAGE } from "@covertree/consts";

import { useGetRenewalListItemsQuery } from "@hooks/renewals/useGetRenewalListItems";
import { useRetryRenewalSendOffer } from "@hooks/actions/useRetryRenewalSendOffer";
import { useRetryRenewalIssuance } from "@hooks/actions/useRetryRenewalIssuance";

import {
  RenewalFilterByStatus,
  RenewalListItemsSortBy,
  SortOrder,
} from "@covertree/agent-portal-graphql";

import { useGetRenewalsColumns } from "./useGetRenewalsColumns";
import { useMappedRenewals } from "./useMappedRenewals";
import { useGetRenewalsFilters } from "./useGetRenewalsFilters";
import { useDownloadRenewalOfferDocument } from "@hooks/actions/useDownloadRenewalOfferDocument";
import { useRetryFailedRenewal } from "@hooks/actions/useRetryFailedRenewal";
import { useResendRenewalOffer } from "@hooks/actions/useResendRenewalOffer";

export const useGetRenewals = () => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);

  const { retryRenewalSendOffer, loading: isRetrySendOfferLoading } =
    useRetryRenewalSendOffer(true);
  const { retryIssuance, loading: isRetryIssuanceLoading } =
    useRetryRenewalIssuance();
  const {
    downloadRenewalOfferDocument,
    loading: isDownloadRenewalOfferDocumentLoading,
  } = useDownloadRenewalOfferDocument();
  const { retryFailedRenewal, loading: retryFailedRenewalLoading } =
    useRetryFailedRenewal(true);
  const { resendRenewalOffer, loading: resendRenewalOfferLoading } =
    useResendRenewalOffer();

  const filters = useGetRenewalsFilters();
  const initialPagination = useMemo(
    () => ({
      from: "0",
      filter: filters[0].value,
      order: SortOrder.Descending,
      sortBy: RenewalListItemsSortBy.EffectiveDate,
    }),
    [filters]
  );

  const isAnyActionLoading = useMemo(
    () =>
      isDownloadRenewalOfferDocumentLoading ||
      isRetrySendOfferLoading ||
      isRetryIssuanceLoading ||
      retryFailedRenewalLoading ||
      resendRenewalOfferLoading,
    [
      isDownloadRenewalOfferDocumentLoading,
      isRetrySendOfferLoading,
      isRetryIssuanceLoading,
      retryFailedRenewalLoading,
      resendRenewalOfferLoading,
    ]
  );

  const {
    from,
    order,
    sortBy,
    filter,
    currentPage,
    onChangePage,
    onChangeFilter,
    onChangeSort,
  } = useTableViewPagination(0, rowsPerPage, initialPagination);

  const handleSortChange = useCallback(
    (nextSortBy: string) => () => {
      onChangeSort(nextSortBy);
    },
    [onChangeSort]
  );

  const queryVariables = useMemo(
    () => ({
      pagination: {
        from,
        size: rowsPerPage,
      },
      sorting: {
        sort: order,
        sortBy: sortBy as RenewalListItemsSortBy,
      },
      filter: {
        status: filter as RenewalFilterByStatus,
      },
    }),
    [filter, from, order, sortBy, rowsPerPage]
  );

  const {
    renewals: items,
    totalCount,
    loading,
    error,
  } = useGetRenewalListItemsQuery(queryVariables);

  const columns = useGetRenewalsColumns(filter as RenewalFilterByStatus);
  const renewals = useMappedRenewals(
    items,
    columns,
    retryRenewalSendOffer,
    retryIssuance,
    downloadRenewalOfferDocument,
    retryFailedRenewal,
    resendRenewalOffer
  );

  return {
    renewals,
    loading,
    isAnyActionLoading,
    error: !!error,
    columns,
    filters,
    order,
    sortBy,
    currentPage,
    rowsPerPage,
    totalCount,
    handleSortChange,
    handleChangePage: onChangePage,
    handleFilterChange: onChangeFilter,
    handleRowsPerPageChange: setRowsPerPage,
  };
};
